import type { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'
import { showErrorToast } from '../../../utils/errorToast.tsx'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import type { CompanyTeamEntry } from '../../../../state_manager/data_models/company_team_entry.ts'
import { validateCompanyTeamEntry } from '../../../../state_manager/data_models/company_team_entry.ts'
import { transformCompanyTeamEntry } from '../../../../app_modules/deals/utils/dealFormTransformers.ts'
import {
  ACTION_TYPE_INSERT,
  ACTION_TYPE_UPDATE,
  ACTION_TYPE_DELETE,
  RELATIONSHIP_TYPE_FOUNDER
} from '../../../../state_manager/globals.ts'
import { TOAST_CONFIG } from '../../FormTable/constants.ts'

interface EmailValidationState {
  validatedEmail: string
  exists: boolean
}

const emailValidationStates = new Map<string | number, EmailValidationState>()

/** The category id a member carries, whether it still holds the raw id string or the resolved option. */
export const categoryIdOf = (category: TeamMember['category']): string | null =>
  typeof category === 'string' ? category : category?.value || category?.id || null

export const hasEmailValidationError = (recordKey: string | number, email: string): boolean => {
  const validationState = emailValidationStates.get(recordKey)
  if (!validationState) return false

  return validationState.validatedEmail === email && validationState.exists
}

const setEmailValidationState = (recordKey: string | number, state: EmailValidationState): void => {
  emailValidationStates.set(recordKey, state)
}

const clearEmailValidationState = (recordKey: string | number): void => {
  emailValidationStates.delete(recordKey)
}

const getEmailValidationState = (recordKey: string | number): EmailValidationState | undefined => {
  return emailValidationStates.get(recordKey)
}

// A type alias, not an interface: FormTable's record constraint carries an index signature, and only
// aliases and literals get the implicit one that satisfies it.
export type TeamMember = {
  key: string | number
  /** null on a freshly added row until a contact is picked (clearMemberFields / handleAddMember). */
  contact: {
    value: string | 'new'
    /** Set alongside `value` once a new contact has been created. */
    id?: string
    label: string
    /** Undecorated contact name beside a decorated `label` — see SelectListEntry.name. */
    name?: string
    data?: {
      id: string
      first_name?: string | null
      last_name?: string | null
      email?: string | null
      mobile_phone?: string | null
      preferred_name?: string | null
      display_name?: string
    }
  } | null
  founder: boolean
  category:
    | {
        value: string
        label: string
        id: string
        category: string
        relationship_type_id: string
      }
    | string
    | null
  jobTitle: string
  email: string
  contactNumber: string
  employeeRelationshipId?: string | null
  founderRelationshipId?: string | null
}

/**
 * One canonical `prog__company_team` record entry (the shape process_new_deal
 * consumes, and therefore what an agent proposal's payload holds) as a table
 * row. The table only understands rows; handed a record entry it had no `key`,
 * and every field config's `firstErrorFieldKey?.rowKey === record.key`
 * compared undefined === undefined and read `.fieldKey` off null — the
 * proposal Review drawer crashed on any deal that created a company team.
 * A row passed in is returned untouched, so callers may mix shapes.
 */
export const companyTeamEntryToMember = (entry: any, index: number): TeamMember => {
  if (entry && (entry.key !== undefined || entry.contact !== undefined)) return entry as TeamMember

  const contactId: string | null = entry?.[CompanyTeamEntryFields.contacts__id] ?? null
  const firstName: string | null = entry?.[CompanyTeamEntryFields.contacts__first_name] ?? null
  const lastName: string | null = entry?.[CompanyTeamEntryFields.contacts__last_name] ?? null
  const email: string | null = entry?.[CompanyTeamEntryFields.contacts__email] ?? null
  const mobile: string | null = entry?.[CompanyTeamEntryFields.contacts__mobile_phone] ?? null
  const name = [firstName, lastName].filter(Boolean).join(' ')
  const isNew = entry?.[CompanyTeamEntryFields.prog__contacts__action] === ACTION_TYPE_INSERT

  return {
    key: contactId ?? `seed-${index}`,
    contact: {
      // A contact the record would INSERT is a new contact to the table; the
      // controller mints its id and reads the names from `data`.
      value: isNew ? 'new' : contactId ?? '',
      label: name || email || contactId || '',
      name,
      data: {
        id: contactId ?? '',
        first_name: firstName,
        last_name: lastName,
        email,
        mobile_phone: mobile
      }
    },
    founder: Boolean(entry?.[CompanyTeamEntryFields.prog__founder]),
    category: entry?.[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ?? null,
    jobTitle: entry?.[CompanyTeamEntryFields.contact_accounts__title] ?? '',
    email: email ?? '',
    contactNumber: mobile ?? ''
  }
}

export interface RelationshipCategoryOption {
  id: string
  category: string
  relationship_type_id: string
}

/** What the team controller hands the Job Category select: the cfg category row, plus `value` / `label` for the select. */
export type RelationshipCategorySelectOption = SelectListEntry & RelationshipCategoryOption

const getComparableValue = (item: any): string | null => {
  if (!item) return null

  if (typeof item === 'object' && item.value === 'new') {
    return item.label?.toLowerCase().trim()
  }

  if (typeof item === 'object' && item.value && item.value.endsWith && item.value.endsWith('_new')) {
    return item.label?.toLowerCase().trim()
  }

  if (typeof item === 'object') {
    return item.value
  }

  return item
}

export const getComparableContact = (contact: any): string | null => {
  if (!contact) return null

  if (typeof contact === 'object') {
    if (contact.value === 'new') {
      return contact.label?.toLowerCase().trim()
    }
    return contact.value
  }

  return contact
}

export const isDuplicateTeamMember = (
  newMember: TeamMember,
  existingMembers: TeamMember[],
  excludeKey: string | number | null = null
): boolean => {
  const newContact = getComparableContact(newMember.contact)
  const newEmail = (newMember.email || '').toLowerCase().trim()

  if (!newContact && !newEmail) {
    return false
  }

  return existingMembers.some((member) => {
    if (excludeKey && member.key === excludeKey) {
      return false
    }

    const existingContact = getComparableContact(member.contact)
    const existingEmail = (member.email || '').toLowerCase().trim()

    const isDuplicateContact = newContact && existingContact && existingContact === newContact
    const isDuplicateEmail = newEmail && existingEmail && existingEmail === newEmail

    return isDuplicateContact || isDuplicateEmail
  })
}

const generateContactDisplayName = (contact: {
  preferred_name?: string | null
  first_name?: string | null
  last_name?: string | null
}): string => {
  if (!contact) return ''

  const firstName = contact.first_name || ''
  const lastName = contact.last_name || ''
  const preferredName = contact.preferred_name || ''

  let displayName = preferredName || firstName

  if (lastName) {
    if (displayName) {
      displayName = `${displayName} ${lastName}`
    } else {
      displayName = lastName
    }
  }

  return displayName
}

export const parseContactNameFromLabel = (
  contact: NonNullable<TeamMember['contact']>
): { firstName: string | null; lastName: string | null } => {
  // A seeded new contact (companyTeamEntryToMember) carries its names already
  // split — trust them over re-splitting the label, which mis-parses a
  // multi-word first name.
  if (contact.value === 'new' && (contact.data?.first_name || contact.data?.last_name)) {
    return { firstName: contact.data.first_name || null, lastName: contact.data.last_name || null }
  }
  if (contact.value === 'new' && contact.label) {
    const cleanLabel = contact.label.replace(/\s*\(new\)\s*$/i, '').trim()
    const nameParts = cleanLabel.split(/\s+/)
    if (nameParts.length === 1) {
      return { firstName: null, lastName: nameParts[0] }
    } else if (nameParts.length >= 2) {
      return { firstName: nameParts[0], lastName: nameParts.slice(1).join(' ') }
    }
  }
  return {
    firstName: contact.data?.first_name || null,
    lastName: contact.data?.last_name || null
  }
}

export const detectMemberChanges = (
  current: TeamMember,
  original: TeamMember
): {
  contactChanged: boolean
  categoryChanged: boolean
  jobTitleChanged: boolean
  emailChanged: boolean
  mobileChanged: boolean
  founderChanged: boolean
} => {
  return {
    contactChanged: current.contact?.value !== original.contact?.value,
    categoryChanged: getComparableValue(current.category) !== getComparableValue(original.category),
    jobTitleChanged: (current.jobTitle || '') !== (original.jobTitle || ''),
    emailChanged: (current.email || '') !== (original.email || ''),
    mobileChanged: (current.contactNumber || '') !== (original.contactNumber || ''),
    founderChanged: current.founder !== original.founder
  }
}

export const determineContactAction = (
  contactValue: string | 'new',
  emailChanged: boolean,
  mobileChanged: boolean
): string => {
  if (contactValue === 'new') {
    return ACTION_TYPE_INSERT
  } else if (emailChanged || mobileChanged) {
    return ACTION_TYPE_UPDATE
  }
  return ''
}

export interface BuildRelationshipParams {
  relationshipId: string | number | null
  relationshipType: string
  categoryId: string | null
  action: string
  companyId: string
  contactId: string
  member: TeamMember
  firstName: string | null
  lastName: string | null
  contactAction: string
  isFounder: boolean
  isExEmployees: boolean
}

/** One contact-relationship write for the team API, as buildRelationshipObject assembles it. */
export interface TeamRelationshipPayload {
  id: string
  action: string
  table_name: string
  record_id: string
  contact_id: string
  relationship_type: string
  relationship_category_id: string | null
  title: string | null
  contacts: {
    id: string
    email: string | null
    last_name: string | null
    first_name: string | null
    mobile_phone: string | null
    preferred_name: string | null
    display_name: string
  }
  founder: boolean
  contact_action: string
  current: boolean
}

export const buildRelationshipObject = (params: BuildRelationshipParams): TeamRelationshipPayload => {
  const {
    relationshipId,
    relationshipType,
    categoryId,
    action,
    companyId,
    contactId,
    member,
    firstName,
    lastName,
    contactAction,
    isFounder,
    isExEmployees
  } = params

  return {
    id: relationshipId ? String(relationshipId) : crypto.randomUUID(),
    action,
    table_name: 'accounts',
    record_id: companyId,
    contact_id: contactId,
    relationship_type: relationshipType,
    relationship_category_id: categoryId,
    title: member.jobTitle || null,
    contacts: {
      id: contactId,
      email: member.email || null,
      last_name: lastName,
      first_name: firstName,
      mobile_phone: member.contactNumber || null,
      preferred_name: member.contact?.data?.preferred_name || null,
      display_name: generateContactDisplayName({
        preferred_name: member.contact?.data?.preferred_name,
        first_name: firstName,
        last_name: lastName
      })
    },
    founder: isFounder,
    contact_action: contactAction,
    current: !isExEmployees
  }
}

export const transformAndValidateRelationships = (relationships: any[]): any[] => {
  return relationships
    .map((relationship) => {
      try {
        const isDeleteAction =
          relationship[CompanyTeamEntryFields.prog__contact_accounts__action] === ACTION_TYPE_DELETE ||
          relationship.action === ACTION_TYPE_DELETE

        if (isDeleteAction && !relationship.table_name) {
          if (relationship[CompanyTeamEntryFields.contact_accounts__id]) {
            validateCompanyTeamEntry(relationship)
            return relationship
          }
          const schemaEntry = {
            [CompanyTeamEntryFields.contact_accounts__id]:
              relationship.id || relationship[CompanyTeamEntryFields.contact_accounts__id],
            [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
          }
          validateCompanyTeamEntry(schemaEntry)
          return schemaEntry
        }

        const schemaEntry = transformCompanyTeamEntry(relationship)
        if (!schemaEntry) {
          return null
        }

        validateCompanyTeamEntry(schemaEntry)
        return schemaEntry
      } catch (error) {
        console.error('Validation failed for team member entry:', error)
        const errorMessage = error instanceof Error ? error.message : 'Validation failed'
        showErrorToast(`Team member validation failed: ${errorMessage}`, TOAST_CONFIG)
        throw error
      }
    })
    .filter(Boolean)
}

export const handleFounderStatusChange = (
  changes: ReturnType<typeof detectMemberChanges>,
  currentMember: TeamMember,
  originalMember: TeamMember,
  relationships: any[],
  companyId: string,
  contactId: string,
  categoryId: string | null,
  founderCategoryOption: RelationshipCategoryOption | null,
  firstName: string | null,
  lastName: string | null,
  contactAction: string,
  isExEmployees: boolean
): void => {
  if (!changes.founderChanged) return

  if (currentMember.founder && !originalMember.founder) {
    const founderCategoryId = founderCategoryOption?.id || categoryId
    relationships.push(
      buildRelationshipObject({
        relationshipId: null,
        relationshipType: RELATIONSHIP_TYPE_FOUNDER,
        categoryId: founderCategoryId,
        action: ACTION_TYPE_INSERT,
        companyId,
        contactId,
        member: currentMember,
        firstName,
        lastName,
        contactAction,
        isFounder: true,
        isExEmployees
      })
    )
  } else if (!currentMember.founder && originalMember.founder) {
    const founderRelationshipId = currentMember.founderRelationshipId || originalMember.founderRelationshipId
    if (founderRelationshipId) {
      relationships.push({
        [CompanyTeamEntryFields.contact_accounts__id]: founderRelationshipId,
        [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
      })
    }
  }
}

export const transformExCompanyTeamToTeamMembers = (
  exCompanyTeam: CompanyTeamEntry[] | null | undefined,
  relationshipCategories: RelationshipCategoryOption[] = []
): TeamMember[] => {
  if (!exCompanyTeam || !Array.isArray(exCompanyTeam)) {
    return []
  }

  const getStringValue = (value: unknown): string | null => {
    return typeof value === 'string' ? value : null
  }

  const getBooleanValue = (value: unknown): boolean => {
    return typeof value === 'boolean' ? value : false
  }

  return exCompanyTeam.map((entry): TeamMember => {
    const contactsId = getStringValue(entry[CompanyTeamEntryFields.contacts__id])
    const contactAccountsId = getStringValue(entry[CompanyTeamEntryFields.contact_accounts__id])
    const contactId = contactsId || contactAccountsId || null

    const firstName = getStringValue(entry[CompanyTeamEntryFields.contacts__first_name]) || ''
    const lastName = getStringValue(entry[CompanyTeamEntryFields.contacts__last_name]) || ''
    const email = getStringValue(entry[CompanyTeamEntryFields.contacts__email]) || ''
    const displayName =
      getStringValue(entry[CompanyTeamEntryFields.prog__display_name]) ||
      `${firstName} ${lastName}`.trim() ||
      email ||
      'Unknown'

    const categoryId = getStringValue(entry[CompanyTeamEntryFields.contact_accounts__relationship_category_id])
    let category: TeamMember['category'] = null
    if (categoryId && relationshipCategories.length > 0) {
      const categoryOption = relationshipCategories.find((cat) => cat.id === categoryId)
      if (categoryOption) {
        category = {
          value: categoryOption.id,
          label: categoryOption.category,
          id: categoryOption.id,
          category: categoryOption.category,
          relationship_type_id: categoryOption.relationship_type_id
        }
      } else if (categoryId) {
        category = categoryId
      }
    }

    const keyValue = contactAccountsId || `ex-employee-${contactId || 'unknown'}`

    return {
      key: keyValue,
      contact: {
        value: contactId || 'new',
        label: displayName,
        data: {
          id: contactId || '',
          first_name: firstName || null,
          last_name: lastName || null,
          email: email || null,
          mobile_phone: getStringValue(entry[CompanyTeamEntryFields.contacts__mobile_phone]) || null,
          preferred_name: getStringValue(entry[CompanyTeamEntryFields.contacts__preferred_name]) || null,
          display_name: displayName
        }
      },
      founder: getBooleanValue(entry[CompanyTeamEntryFields.prog__founder]),
      category: category,
      jobTitle: getStringValue(entry[CompanyTeamEntryFields.contact_accounts__title]) || '',
      email: email,
      contactNumber: getStringValue(entry[CompanyTeamEntryFields.contacts__mobile_phone]) || ''
    }
  })
}
